import { useMutation, useQueryClient } from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";

import { RECEIPTS_POLL_KEY, RECEIPTS_QUERY_KEY } from "./useReceiptsList";

export type BulkAcceptErrorCode = "not_found" | "conflict" | "internal_error";

export type BulkAcceptResult =
  | {
      suggestion_id: number;
      status: "success";
      receipt_uuid: string;
    }
  | {
      suggestion_id: number;
      status: "failed";
      error_code: BulkAcceptErrorCode;
      error_message: string;
    };

export interface BulkAcceptResponse {
  results: BulkAcceptResult[];
  summary: {
    succeeded: number;
    failed: number;
    total: number;
  };
}

export interface BulkAcceptVariables {
  suggestionIds: number[];
  /** Click-time snapshot of suggestion_id → receipt uuid. Stored on the
   * mutation so onSuccess can resolve failed-row receipt uuids without
   * reading the live selectionState (which the refetch-prune effect may
   * have shrunk while the request was in-flight). */
  receiptUuidBySuggestionId: Record<number, string>;
}

export interface UseBulkAcceptMatchesOptions {
  onSuccess?: (
    response: BulkAcceptResponse,
    variables: BulkAcceptVariables
  ) => void;
  onError?: (error: Error) => void;
}

// ApiHttp.fetch can throw non-Error values (a plain string, the unwrapped
// response.data object) depending on transport. Normalize to Error so callers
// can rely on err.message without type-guarding every site.
const toBulkAcceptError = (err: unknown): Error => {
  if (err instanceof Error) return err;
  if (typeof err === "string") return new Error(err);
  if (
    err &&
    typeof err === "object" &&
    "message" in err &&
    typeof (err as { message: unknown }).message === "string"
  ) {
    return new Error((err as { message: string }).message);
  }
  return new Error("Bulk accept failed");
};

export const useBulkAcceptMatches = (
  options: UseBulkAcceptMatchesOptions = {}
) => {
  const queryClient = useQueryClient();

  return useMutation<BulkAcceptResponse, Error, BulkAcceptVariables>({
    mutationFn: async ({ suggestionIds }) => {
      try {
        const response = await ApiHttp.fetch<BulkAcceptResponse>(
          "receipt-transaction-suggestions/accept-batch/",
          { method: "POST" },
          { suggestion_ids: suggestionIds }
        );
        return response;
      } catch (err) {
        throw toBulkAcceptError(err);
      }
    },
    onSuccess: (response, variables) => {
      // Always invalidate list caches — successful accepts moved rows out of
      // their bucket and failed rows may have had their server state changed
      // by a concurrent action. Spare the upload-polling cache (same scoping
      // as useAcceptMatch).
      queryClient.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === RECEIPTS_QUERY_KEY &&
          q.queryKey[1] !== RECEIPTS_POLL_KEY,
      });
      options.onSuccess?.(response, variables);
    },
    onError: (err) => {
      options.onError?.(err);
    },
  });
};
